import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IAddOPDWizard,
  ICompareDocsItems,
  IOPDAnsweredQuestion,
  IOPDBill,
  IOPDBillListFetchResp,
  IOPDBillMapping,
  IOPDBillMappingFetchResp,
  IOPDBillsState,
  IOPDDoc,
  IOPDDocS3Data,
  IOPDErrorResp,
  IOPDFetchQuestionAnswersResp,
  IOPDHealthCareBillHeader,
  IOPDHealthCareBillHeaderFetchResp,
  IOPDListFilters,
  IOPDManualQuestion,
  IOPDPatientPayableFetchResp,
  IOPDSchemeBillHeader,
  IOPDSchemeBillImplantItem,
  IOPDSchemeBillMedicalItem,
  IOPDSchemeBillPackageItem,
  IOPDSchemeBillPatientPayableItem,
  IOPDSchemeHeaderFetchResp,
  IOPDSchemeHeaderUpdate,
  IOPDSchemeOPDBillItem,
  ISchemeMappingsInHealthCare,
} from "../interfaces/IOPDBills";
import {
  fetchOPDBills,
  fetchOPDQuestionnaires,
  getEditOPDBillMapping,
  getHealthCarePatientPayable,
  getOPDBillSchemeHeader,
  getOPDQuestionAnswerCollection,
  getNewOPDBillHealthCareBillHeader,
  getNewOPDBillMapping,
  postQuestionAnswersCollection,
  updateSchemeBillItems,
  uploadOPDDocToServer,
  callApproveOPDBill,
  downloadAbstractExcel,
  downloadAbstractZip,
  emptySchemeBillItem,
} from "../apis/opdBillingService";
import { ADD_OPD_BILL_WIZARD_ITEMS } from "../utils/constants";
import { errorMessage, successMessage } from "../utils/notificationUtils";
import {
  fetchNewOPDHealthCareBillHeaderUtil,
  resetOPDBillStateUtil,
  updateOPDBillMappingsUtil,
  updateSchemeDataUtil,
  updateSchemePackageUtil,
} from "../utils/opdBillingUtils";

const initialState: IOPDBillsState = {
  isFetchingBillList: false,
  pageToOPDBillList: {},
  userDetailfilledManually: {},
  addOPDBill: {
    localFiles: {},
    localFiles2: [],

    allWizardSteps: ADD_OPD_BILL_WIZARD_ITEMS,
    opdManualQuestions: {
      opdQuestionnaires: {},
      isFetchingQuestionnaires: false,
      currentQuestionId: 1,
      answeredQuestionsResponse: {
        cardCategory: "",
        conclusion: "",
        healthCareBillItemsResponses: {
          healthCareBillOpdItems: [],
          healthCareBillMedicalItems: [],
        },
        packageDates: [],
        questionAnswersCollectedResponses: [],
        tataMemorialRateApply: "",
      },
      isConclusionGenerated: false,
    },
    billMappings: null,
    finalScreen: {
      healthCareBillIdDataMap: {},
      schemeBillHeaderIdDataMap: {},
      billMappingMap: {},
      allHealthCareBillItems: {
        opd: {},
        medical: {},
      },
      allSchemeBillItems: {
        cghs: {
          implant: {},
          opd: {},
          package: {},
          medical: {},
        },
        patientPayable: {
          opd: {},
          medical: {},
        },
      },
    },
    errors: {
      errorBillMapping: null,
      errorHealthCareBillHeader: null,
      errorHealthCarePatientPayable: null,
      errorQuestionAnswerCollection: null,
      errorBillSchemeHeader: null,
      errorPostQuestionAnswerCollection: null,
      errorInUpdatingSchemeBillItems: null,
      errorInApprovingOPDBill: null,
    },
    currState: {
      currBillMappingId: null,
      currSchemeBillHeaderId: null,
      currHealthCareBillHeaderId: null,
      wizardCurrScreen: 0,
      claimId: null,
      arePackagedItemsHighlighted: false,
      arePatientPayableItemsHighlighted: false,
    },
    loaders: {
      isSchemeBillItemsUpdating: false,
      isFetchOPDBillData: false,
      isApprovingOPDBill: false,
      isResetData: false,
    },
    relatedDocs: {},
    filters: {
      searchFiltersByComponents: {
        scheme: {
          cghs: {
            opd: "",
            implant: "",
            package: "",
            medical: "",
          },
          patientPayable: {
            opd: "",
            medical: "",
          },
        },
        healthCare: {
          opd: "",
          medical: "",
        },
      },
    },
  },
  opdListFilters: {
    searchTerm: "",
    orderBy: "DESC",
    pageNumber: 1,
    pageSize: 10,
    startDate: null,
    endDate: null,
  },
  opdBillListCurrState: {
    totalElements: 0,
  },
  necessaryDetailsForOPD: {},
};

export const getOPDBillList = createAsyncThunk(
  "opdBills/getOPDBillsList",
  async ({
    hospitalId,
    filters,
  }: {
    hospitalId: number;
    filters: IOPDListFilters;
  }) => {
    const response = await fetchOPDBills(hospitalId, filters);
    return response;
  }
);

export const approveOPDBill = createAsyncThunk(
  "opdBills/approveOPDBill",
  async ({
    hospitalId,
    currBillMappingId,
    currHealthCareBillHeaderId,
    currSchemeBillHeaderId,
    status,
  }: {
    hospitalId: number | undefined;
    currBillMappingId: number | undefined;
    currHealthCareBillHeaderId: number | undefined;
    currSchemeBillHeaderId: number | undefined;
    status: string;
  }) => {
    const response = await callApproveOPDBill(
      hospitalId,
      currBillMappingId,
      currHealthCareBillHeaderId,
      currSchemeBillHeaderId,
      status
    );
    return response;
  }
);

export const getOPDQuestionnaire = createAsyncThunk(
  "opdBills/getOPDQuestionnaire",
  async ({ accessToken }: { accessToken: string }) => {
    const response = await fetchOPDQuestionnaires(accessToken);
    return response;
  }
);

export const generateNewOPDBillMapping = createAsyncThunk(
  "opdBills/generateNewOPDBillMapping",
  async ({
    accessToken,
    hospitalId,
    documentPaths,
    billMapping,
  }: {
    accessToken: string;
    hospitalId: number;
    documentPaths: Array<IOPDDocS3Data>;
    billMapping: IOPDBillMapping | {};
  }) => {
    const response = await getNewOPDBillMapping(
      accessToken,
      hospitalId,
      documentPaths,
      billMapping
    );
    return response;
  }
);

export const fetchEditOPDBillMapping = createAsyncThunk(
  "opdBills/fetchEditOPDBillMapping",
  async ({
    accessToken,
    hospitalId,
    opdBillId,
  }: {
    accessToken: string;
    hospitalId: number;
    opdBillId: string;
  }) => {
    const response = await getEditOPDBillMapping(
      accessToken,
      hospitalId,
      opdBillId
    );
    return response;
  }
);

export const fetchNewOPDHealthCareBillHeader = createAsyncThunk(
  "opdBills/fetchNewOPDHealthCareBillHeader",
  async ({
    accessToken,
    healthCareBillHeaderId,
  }: {
    accessToken: string;
    healthCareBillHeaderId: number;
  }) => {
    const response = await getNewOPDBillHealthCareBillHeader(
      accessToken,
      healthCareBillHeaderId
    );
    return response;
  }
);

export const fetchHealthCarePatientPayable = createAsyncThunk(
  "opdBills/fetchHealthCarePatientPayable ",
  async ({
    accessToken,
    healthCareBillHeaderId,
  }: {
    accessToken: string;
    healthCareBillHeaderId: number;
  }) => {
    const response = await getHealthCarePatientPayable(
      accessToken,
      healthCareBillHeaderId
    );
    return response;
  }
);

export const fetchOPDQuestionAnswerCollection = createAsyncThunk(
  "opdBills/fetchOPDQuestionAnswerCollection ",
  async ({
    accessToken,
    billMappingId,
  }: {
    accessToken: string;
    billMappingId: number;
  }) => {
    const response = await getOPDQuestionAnswerCollection(
      accessToken,
      billMappingId
    );
    return response;
  }
);

export const fetchOPDBillSchemeHeader = createAsyncThunk(
  "opdBills/fetchOPDBillSchemeHeader ",
  async ({
    accessToken,
    schemeBillHeaderId,
  }: {
    accessToken: string;
    schemeBillHeaderId: number;
  }) => {
    const response = await getOPDBillSchemeHeader(
      accessToken,
      schemeBillHeaderId
    );
    return response;
  }
);
export const resetSchemeBillItem = createAsyncThunk(
  "opdBills/resetSchemeBillItem ",
  async ({
    accessToken,
    schemeBillHeaderId,
  }: {
    accessToken: string;
    schemeBillHeaderId: number | null;
  }) => {
    const response = await emptySchemeBillItem(accessToken, schemeBillHeaderId);
    return response;
  }
);

export const getAbstractExcel = createAsyncThunk(
  "opdBills/getAbstractExcel ",
  async ({ currSchemeBillHeaderId }: { currSchemeBillHeaderId: number }) => {
    const response = await downloadAbstractExcel(currSchemeBillHeaderId);
    return response;
  }
);

export const getAbstractZip = createAsyncThunk(
  "opdBills/getAbstractZip ",
  async ({
    hospitalId,
    dateFrom,
    dateTo,
  }: {
    hospitalId: number | undefined;
    dateFrom: string | undefined;
    dateTo: string | undefined;
  }) => {
    const response = await downloadAbstractZip(hospitalId, dateFrom, dateTo);
    return response;
  }
);

export const submitQuestionAnswersCollection = createAsyncThunk(
  "opdBills/submitQuestionAnswersCollection",
  async ({
    answeredQuestions,
  }: {
    answeredQuestions: Array<IOPDAnsweredQuestion>;
  }) => {
    const response = await postQuestionAnswersCollection(answeredQuestions);
    return response;
  }
);

export const uploadOPDDoc = createAsyncThunk(
  "opdBills/uploadOPDDoc",
  async ({
    file,
    accessToken,
    docType,
    patientId,
    caseId,
    hospitalId,
    visitId,
  }: {
    file: IOPDDoc;
    accessToken: string;
    hospitalId: string | null;
    docType: string;
    patientId: string | null;
    caseId: string | null;
    visitId: string | null;
  }) => {
    const response = await uploadOPDDocToServer(
      file.originFileObj,
      accessToken,
      docType,
      patientId,
      caseId,
      hospitalId,
      visitId
    );
    return response;
  }
);

export const uploadRelatedDoc = createAsyncThunk(
  "opdBills/uploadRelatedDoc",
  async ({
    file,
    docType,
    accessToken,
    hospitalId,
    visitId,
  }: {
    file: object;
    docType: string;
    accessToken: string;
    hospitalId: string;
    visitId: string | null;
  }) => {
    const response = await uploadOPDDocToServer(
      file,
      accessToken,
      docType,
      "0",
      "0",
      hospitalId,
      visitId
    );
    return response;
  }
);

export const updateSchemeBills = createAsyncThunk(
  "opdBills/updateSchemeBills",
  async (
    {
      hospitalId,
      billMappingId,
      schemeBillHeaderId,
      schemeBillData,
      relatedDocs,
      accessToken,
      visitId,
    }: {
      hospitalId: number | undefined;
      billMappingId: number | null;
      schemeBillHeaderId: number | null;
      schemeBillData: IOPDSchemeHeaderUpdate;
      relatedDocs: { [key: string]: ICompareDocsItems };
      accessToken: string;
      visitId: string | null;
    },
    { dispatch, getState }
  ) => {
    try {
      const uploadPromises = Object.keys(relatedDocs)?.map((key) => {
        if (relatedDocs[key].isUploaded === false && relatedDocs[key].fileObj) {
          return dispatch(
            uploadRelatedDoc({
              file: relatedDocs[key].fileObj,
              accessToken: accessToken,
              hospitalId: hospitalId?.toString() || "",
              docType: key,
              visitId: visitId,
            })
          );
        }
        return Promise.resolve();
      });
      await Promise.all(uploadPromises);
    } catch (error) {}
    const updatedState: any = getState();
    const currentHeaderId =
      updatedState.opdBills.addOPDBill.currState.currSchemeBillHeaderId;
    // Using updatedState here to get latest data of related docs
    schemeBillData.schemeOpdBillHeader =
      updatedState.opdBills.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
        currentHeaderId
      ].billHeader;

    const response = await updateSchemeBillItems(
      hospitalId,
      billMappingId,
      schemeBillHeaderId,
      schemeBillData
    );
    if (response) {
      dispatch(resetSchemeBills());
    }
    return response;
  }
);

const opdBillsSlice = createSlice({
  name: "opdBills",
  initialState,
  reducers: {
    clearNecessaryDetailsForOPD: (state, action) => {
      state.necessaryDetailsForOPD = {};
    },
    updateAddOPDBillWizardCurrScreenNumber: (
      state,
      action: PayloadAction<number>
    ) => {
      state.addOPDBill.currState.wizardCurrScreen = action.payload;
    },
    updateCardCategory: (state, action: PayloadAction<string>) => {
      if (state.addOPDBill.currState.currSchemeBillHeaderId) {
        state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addOPDBill.currState.currSchemeBillHeaderId
        ].billHeader = {
          ...state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billHeader,
          cardCategory: action.payload,
        };
      }

      successMessage(`Successfully update card category to: ${action.payload}`);
    },
    updateObservations: (state, action: PayloadAction<string>) => {
      if (state.addOPDBill.currState.currSchemeBillHeaderId) {
        state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addOPDBill.currState.currSchemeBillHeaderId
        ].billHeader = {
          ...state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billHeader,
          observation: action.payload,
        };
      }

      successMessage(`Successfully updated observations`);
    },
    addAnsweredQuestion: (
      state,
      action: PayloadAction<{
        newAnswer: IOPDAnsweredQuestion;
        newQusId: number;
      }>
    ) => {
      const { newAnswer, newQusId } = action.payload;
      state.addOPDBill.opdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses.push(
        newAnswer
      );
      state.addOPDBill.opdManualQuestions.currentQuestionId = newQusId;
    },
    editAnsweredQuestion: (
      state,
      action: PayloadAction<{
        ansIdx: number;
        newQusId: number;
      }>
    ) => {
      const { ansIdx, newQusId } = action.payload;
      state.addOPDBill.opdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses.splice(
        ansIdx
      );
      state.addOPDBill.opdManualQuestions.currentQuestionId = newQusId;
    },
    resetAnsweredQuestions: (state) => {
      state.addOPDBill.opdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses =
        [];
      state.addOPDBill.opdManualQuestions.currentQuestionId =
        state.addOPDBill.opdManualQuestions.opdQuestionnaires[1].id;
      state.addOPDBill.opdManualQuestions.isConclusionGenerated = false;
    },
    updateAddOPDBillWizardCurrScreenValues: (
      state,
      action: PayloadAction<{
        currScreen: number;
        newWizardValues: IAddOPDWizard;
      }>
    ) => {
      const { currScreen, newWizardValues } = action.payload;
      state.addOPDBill.allWizardSteps[currScreen] = newWizardValues;
    },
    resetCurrHealthCareBillHeaderId: (state) => {
      state.addOPDBill.currState.currHealthCareBillHeaderId = null;
    },
    resetCurrSchemeBillHeaderId: (state) => {
      state.addOPDBill.currState.currSchemeBillHeaderId = null;
    },
    resetAddOPDBillState: (state) => {
      resetOPDBillStateUtil(state);
    },
    resetLocalFilesState: (state) => {
      state.addOPDBill.localFiles = {};
      state.addOPDBill.localFiles2 = [];
    },
    addOPDBillUploadedFile: (
      state,
      action: PayloadAction<{ file: IOPDDoc; docType: string }>
    ) => {
      const file = action.payload.file;
      const key = action.payload.docType;
      state.addOPDBill.localFiles[`${key}`] = file;
    },
    updateSchemeBillItem: (
      state,
      action: PayloadAction<{
        category: "cghs" | "patientPayable";
        subCategory: "opd" | "implant" | "medical" | "package";
        item:
          | IOPDSchemeBillImplantItem
          | IOPDSchemeOPDBillItem
          | IOPDSchemeBillPackageItem
          | IOPDSchemeBillMedicalItem
          | IOPDSchemeBillPatientPayableItem;
      }>
    ) => {
      const { category, subCategory, item } = action.payload;
      if (category === "cghs") {
        state.addOPDBill.finalScreen.allSchemeBillItems[category][subCategory][
          item.id
        ] = item;
      } else if (
        category === "patientPayable" &&
        (subCategory === "opd" || subCategory === "medical")
      ) {
        state.addOPDBill.finalScreen.allSchemeBillItems.patientPayable[
          subCategory
        ][item.id] = item;
      }
    },
    removeOPDBillUploadedFile: (
      state,
      action: PayloadAction<{ docType: string }>
    ) => {
      const key = action.payload.docType;
      const { [key]: removed, ...rest } = state.addOPDBill.localFiles;
      state.addOPDBill.localFiles = rest;
    },
    removeOPDBillUploadedFile2: (
      state,
      action: PayloadAction<{ docType: IOPDDoc }>
    ) => {
      const key = action?.payload?.docType;

      if (key?.id) {
        state.addOPDBill.localFiles2 = state.addOPDBill.localFiles2?.filter(
          (item: any) => item.id !== key?.id
        );
      }
      // const { [key]: removed, ...rest } = state.addOPDBill.localFiles;
      // state.addOPDBill.localFiles = rest;
    },
    removeSchemeBillItem: (
      state,
      action: PayloadAction<{
        item:
          | IOPDSchemeBillImplantItem
          | IOPDSchemeBillPackageItem
          | IOPDSchemeBillMedicalItem
          | IOPDSchemeOPDBillItem
          | IOPDSchemeBillPatientPayableItem
          | any;
        type:
          | "cghsOPD"
          | "cghsImplant"
          | "cghsMedical"
          | "cghsPackage"
          | "patientPayableOPD"
          | "patientPayableMedical";
      }>
    ) => {
      const { item, type } = action.payload;

      try {
        if (!state.addOPDBill.currState.currSchemeBillHeaderId) {
          throw new Error(
            "currSchemeBillHeaderId is not valid: " +
              state.addOPDBill.currState.currSchemeBillHeaderId
          );
        }

        //First loop over lids and update schemeMappings in allHealthCareBillItems
        item.lid?.map((lid: number) => {
          //Check if lid exists in healthcare opd
          if (
            state.addOPDBill.finalScreen.allHealthCareBillItems.opd.hasOwnProperty(
              lid
            )
          ) {
            state.addOPDBill.finalScreen.allHealthCareBillItems.opd[
              lid
            ].schemeMappings =
              state.addOPDBill.finalScreen.allHealthCareBillItems.opd[
                lid
              ].schemeMappings?.filter(
                (schemeMapping) =>
                  schemeMapping.itemMappingInSchemeId !== item.id
              );
          } else if (
            state.addOPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
              lid
            )
          ) {
            state.addOPDBill.finalScreen.allHealthCareBillItems.medical[
              lid
            ].schemeMappings =
              state.addOPDBill.finalScreen.allHealthCareBillItems.medical[
                lid
              ].schemeMappings?.filter(
                (schemeMapping) =>
                  schemeMapping.itemMappingInSchemeId !== item.id
              );
          }
        });

        // Then remove the item from allHealthCareBillItems and schemeBillHeaderIdDataMap
        switch (type) {
          case "cghsOPD":
            delete state.addOPDBill.finalScreen.allSchemeBillItems.cghs.opd[
              item.id
            ];
            state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addOPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.opd[item.serviceCategory] =
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.opd[item.serviceCategory]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.opd[item.serviceCategory].length === 0
            ) {
              delete state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.opd[item.serviceCategory];
            }
            successMessage("Deleted Item from scheme bill", 3);
            return;
          case "cghsImplant":
            delete state.addOPDBill.finalScreen.allSchemeBillItems.cghs.implant[
              item.id
            ];
            state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addOPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.implant[item.serviceCategory] =
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.implant[item.serviceCategory]?.filter(
                (id) => id !== item.id
              );
            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.implant[item.serviceCategory].length === 0
            ) {
              delete state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.implant[item.serviceCategory];
            }
            return;
          case "cghsMedical":
            delete state.addOPDBill.finalScreen.allSchemeBillItems.cghs.medical[
              item.id
            ];
            state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addOPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.medical[item.serviceCategory] =
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.medical[item.serviceCategory]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.medical[item.serviceCategory].length === 0
            ) {
              delete state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.medical[item.serviceCategory];
            }
            return;
          case "cghsPackage":
            delete state.addOPDBill.finalScreen.allSchemeBillItems.cghs.package[
              item.id
            ];
            state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addOPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.package[item.serviceCategory] =
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.package[item.serviceCategory]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.package[item.serviceCategory].length === 0
            ) {
              delete state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.package[item.serviceCategory];
            }
            return;
          case "patientPayableOPD":
            delete state.addOPDBill.finalScreen.allSchemeBillItems
              .patientPayable.opd[item.id];
            state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addOPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.opd[item.serviceCategory] =
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.opd[item.serviceCategory]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.opd[item.serviceCategory].length === 0
            ) {
              delete state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.opd[item.serviceCategory];
            }
            return;
          case "patientPayableMedical":
            delete state.addOPDBill.finalScreen.allSchemeBillItems
              .patientPayable.medical[item.id];
            state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addOPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.medical[item.serviceCategory] =
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.medical[item.serviceCategory]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.medical[item.serviceCategory]
                .length === 0
            ) {
              delete state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addOPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.medical[item.serviceCategory];
            }
            return;
          default:
            throw new Error("Type not supported: " + type);
        }
      } catch (error) {
        errorMessage("Error in removing item");
      }
    },
    updateSchemePackage: (
      state,
      action: PayloadAction<{ packages: Array<IOPDSchemeBillPackageItem> }>
    ) => {
      updateSchemePackageUtil(state, action);
    },
    toggleHighlightPackagedItems: (state) => {
      state.addOPDBill.currState.arePackagedItemsHighlighted =
        !state.addOPDBill.currState.arePackagedItemsHighlighted;
    },
    toggleHighlightPatientPayableItems: (state) => {
      state.addOPDBill.currState.arePatientPayableItemsHighlighted =
        !state.addOPDBill.currState.arePatientPayableItemsHighlighted;
    },
    saveRelatedDocLocally: (
      state,
      action: PayloadAction<{ key: string; doc: ICompareDocsItems }>
    ) => {
      const schemeBillHeaderId =
        state.addOPDBill.currState.currSchemeBillHeaderId;
      const { key, doc } = action.payload;
      if (schemeBillHeaderId && key && doc) {
        state.addOPDBill.relatedDocs[schemeBillHeaderId][key] = doc;
      }
    },
    updateClaimId: (state, action: PayloadAction<{ claimId: string }>) => {
      if (state.addOPDBill.currState.currSchemeBillHeaderId) {
        state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addOPDBill.currState.currSchemeBillHeaderId
        ].billHeader = {
          ...state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billHeader,
          claimId: action.payload.claimId,
        };
        state.addOPDBill.currState.claimId = action.payload.claimId;
        successMessage("Claim Id updated!");
      } else {
        // errorMessage("Cannot update claim id");
      }
    },
    updateLidsInPackage: (
      state,
      action: PayloadAction<{ newLids: Array<number>; packageId: number }>
    ) => {
      try {
        const { newLids, packageId } = action.payload;
        if (
          state.addOPDBill.finalScreen.allSchemeBillItems.cghs.package.hasOwnProperty(
            packageId
          )
        ) {
          const removedLids =
            state.addOPDBill.finalScreen.allSchemeBillItems.cghs.package[
              packageId
            ].lid?.filter((id) => !newLids.includes(id));

          // Loop over removed lids and update allHealthCareBillItems
          removedLids?.forEach((id) => {
            //Check if lid exists in healthcare opd
            if (
              state.addOPDBill.finalScreen.allHealthCareBillItems.opd.hasOwnProperty(
                id
              )
            ) {
              state.addOPDBill.finalScreen.allHealthCareBillItems.opd[
                id
              ].schemeMappings =
                state.addOPDBill.finalScreen.allHealthCareBillItems.opd[
                  id
                ].schemeMappings?.filter(
                  (schemeMapping) =>
                    schemeMapping.itemMappingInSchemeId !== packageId
                );
            } else if (
              state.addOPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addOPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings =
                state.addOPDBill.finalScreen.allHealthCareBillItems.medical[
                  id
                ].schemeMappings?.filter(
                  (schemeMapping) =>
                    schemeMapping.itemMappingInSchemeId !== packageId
                );
            }
          });

          state.addOPDBill.finalScreen.allSchemeBillItems.cghs.package[
            packageId
          ].lid = newLids;
        }
      } catch (error) {
        console.error(error);
        errorMessage("Cannot remove items");
      }
    },
    addCGHSOPDItemInScheme: (
      state,
      action: PayloadAction<{ item: IOPDSchemeOPDBillItem }>
    ) => {
      try {
        if (!state.addOPDBill.currState.currSchemeBillHeaderId) {
          throw new Error("currSchemeBillHeaderId is not valid");
        }
        const { item } = action.payload;
        state.addOPDBill.finalScreen.allSchemeBillItems.cghs.opd[item.id] =
          item;

        if (
          !state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.opd.hasOwnProperty(item.serviceCategory)
        ) {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.opd[item.serviceCategory] = [item.id];
        } else {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.opd[item.serviceCategory].push(item.id);
        }

        const schemeMapping: ISchemeMappingsInHealthCare = {
          itemMappingInSchemeCategory: "cghs",
          itemMappingInSchemeSubCategory: "opd",
          itemMappingInSchemeServiceCategory: item.serviceCategory,
          itemMappingInSchemeId: item.id,
        };

        item.lid?.forEach((lid) => {
          state.addOPDBill.finalScreen.allHealthCareBillItems.opd[
            lid
          ].schemeMappings = [
            ...state.addOPDBill.finalScreen.allHealthCareBillItems.opd[lid]
              .schemeMappings,
            schemeMapping,
          ];
        });
      } catch (error) {
        console.error(error);
        errorMessage("Cannot add CGHS OPD item", 4);
      }
    },
    addCGHSOpdToMedicalItemInScheme: (
      state,
      action: PayloadAction<{ item: IOPDSchemeOPDBillItem }>
    ) => {
      try {
        if (!state.addOPDBill.currState.currSchemeBillHeaderId) {
          throw new Error("currSchemeBillHeaderId is not valid");
        }
        const { item } = action.payload;
        state.addOPDBill.finalScreen.allSchemeBillItems.cghs.opd[item.id] =
          item;

        if (
          !state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.opd.hasOwnProperty(item.serviceCategory)
        ) {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.opd[item.serviceCategory] = [item.id];
        } else {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.opd[item.serviceCategory].push(item.id);
        }

        const schemeMapping: ISchemeMappingsInHealthCare = {
          itemMappingInSchemeCategory: "cghs",
          itemMappingInSchemeSubCategory: "opd",
          itemMappingInSchemeServiceCategory: item.serviceCategory,
          itemMappingInSchemeId: item.id,
        };

        item.lid?.forEach((lid) => {
          state.addOPDBill.finalScreen.allHealthCareBillItems.medical[
            lid
          ].schemeMappings = [
            ...state.addOPDBill.finalScreen.allHealthCareBillItems.medical[lid]
              .schemeMappings,
            schemeMapping,
          ];
        });
      } catch (error) {
        console.error(error);
        errorMessage("Cannot add CGHS OPD item", 4);
      }
    },

    // to add medical to opd bills
    // addCGHSMedicalItemInSchemeCGHSOPD: (
    //   state,
    //   action: PayloadAction<{ item: IOPDSchemeOPDBillItem }>
    // ) => {

    // },

    addCGHSMedicalItemInScheme: (
      state,
      action: PayloadAction<{ item: IOPDSchemeBillMedicalItem }>
    ) => {
      if (state.addOPDBill.currState.currSchemeBillHeaderId) {
        const { item } = action.payload;
        state.addOPDBill.finalScreen.allSchemeBillItems.cghs.medical[item.id] =
          item;

        if (
          !state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.medical.hasOwnProperty(item.serviceCategory)
        ) {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.medical[item.serviceCategory] = [item.id];
        } else {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.medical[item.serviceCategory].push(item.id);
        }

        const schemeMapping: ISchemeMappingsInHealthCare = {
          itemMappingInSchemeCategory: "cghs",
          itemMappingInSchemeSubCategory: "medical",
          itemMappingInSchemeServiceCategory: item.serviceCategory,
          itemMappingInSchemeId: item.id,
        };

        item.lid?.forEach((lid) => {
          state.addOPDBill.finalScreen.allHealthCareBillItems.medical[
            lid
          ].schemeMappings = [
            ...state.addOPDBill.finalScreen.allHealthCareBillItems.medical[lid]
              .schemeMappings,
            schemeMapping,
          ];
        });
        successMessage("Moved item to CGHS Medical in Scheme Bill", 4);
      } else {
        errorMessage("Cannot add CGHS medical item");
      }
    },
    addPatientPayableOPDItemInScheme: (
      state,
      action: PayloadAction<{ item: IOPDSchemeBillPatientPayableItem }>
    ) => {
      if (state.addOPDBill.currState.currSchemeBillHeaderId) {
        const { item } = action.payload;
        state.addOPDBill.finalScreen.allSchemeBillItems.patientPayable.opd[
          item.id
        ] = item;

        if (
          !state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.patientPayable.opd.hasOwnProperty(item.serviceCategory)
        ) {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.patientPayable.opd[item.serviceCategory] = [item.id];
        } else {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.patientPayable.opd[item.serviceCategory].push(item.id);
        }

        const schemeMapping: ISchemeMappingsInHealthCare = {
          itemMappingInSchemeCategory: "patientPayable",
          itemMappingInSchemeSubCategory: "opd",
          itemMappingInSchemeServiceCategory: item.serviceCategory,
          itemMappingInSchemeId: item.id,
        };

        item.lid?.forEach((lid) => {
          state.addOPDBill.finalScreen.allHealthCareBillItems.opd[
            lid
          ].schemeMappings = [
            ...state.addOPDBill.finalScreen.allHealthCareBillItems.opd[lid]
              .schemeMappings,
            schemeMapping,
          ];
        });
        successMessage("Moved item to Patient Payable OPD in Scheme Bill", 4);
      } else {
        errorMessage("Cannot add Patient Payable OPD item");
      }
    },
    addPatientPayableMedicalItemInScheme: (
      state,
      action: PayloadAction<{ item: IOPDSchemeBillPatientPayableItem }>
    ) => {
      if (state.addOPDBill.currState.currSchemeBillHeaderId) {
        const { item } = action.payload;
        state.addOPDBill.finalScreen.allSchemeBillItems.patientPayable.medical[
          item.id
        ] = item;

        if (
          !state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.patientPayable.medical.hasOwnProperty(
            item.serviceCategory
          )
        ) {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.patientPayable.medical[item.serviceCategory] = [item.id];
        } else {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.patientPayable.medical[item.serviceCategory].push(
            item.id
          );
        }

        const schemeMapping: ISchemeMappingsInHealthCare = {
          itemMappingInSchemeCategory: "patientPayable",
          itemMappingInSchemeSubCategory: "medical",
          itemMappingInSchemeServiceCategory: item.serviceCategory,
          itemMappingInSchemeId: item.id,
        };

        item.lid?.forEach((lid) => {
          state.addOPDBill.finalScreen.allHealthCareBillItems.medical[
            lid
          ].schemeMappings = [
            ...state.addOPDBill.finalScreen.allHealthCareBillItems.medical[lid]
              .schemeMappings,
            schemeMapping,
          ];
        });
        successMessage(
          "Moved item to Patient Payable Medical in Scheme Bill",
          4
        );
      } else {
        errorMessage("Cannot add Patient Payable OPD item");
      }
    },
    addCGHSImplantItemInScheme: (
      state,
      action: PayloadAction<{
        item: IOPDSchemeBillImplantItem;
        healthCareCategory: "opd" | "medical";
      }>
    ) => {
      if (state.addOPDBill.currState.currSchemeBillHeaderId) {
        const { item, healthCareCategory } = action.payload;
        state.addOPDBill.finalScreen.allSchemeBillItems.cghs.implant[item.id] =
          item;

        if (
          !state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.implant.hasOwnProperty(item.serviceCategory)
        ) {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.implant[item.serviceCategory] = [item.id];
        } else {
          state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addOPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.implant[item.serviceCategory].push(item.id);
        }

        const schemeMapping: ISchemeMappingsInHealthCare = {
          itemMappingInSchemeCategory: "cghs",
          itemMappingInSchemeSubCategory: "implant",
          itemMappingInSchemeServiceCategory: item.serviceCategory,
          itemMappingInSchemeId: item.id,
        };

        item.lid?.forEach((lid) => {
          // Check if lid exists in idp or medical healthcare items

          if (
            state.addOPDBill.finalScreen.allHealthCareBillItems.opd.hasOwnProperty(
              lid
            )
          ) {
            state.addOPDBill.finalScreen.allHealthCareBillItems.opd[
              lid
            ].schemeMappings = [
              ...state.addOPDBill.finalScreen.allHealthCareBillItems.opd[lid]
                .schemeMappings,
              schemeMapping,
            ];
          } else if (
            state.addOPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
              lid
            )
          ) {
            state.addOPDBill.finalScreen.allHealthCareBillItems.medical[
              lid
            ].schemeMappings = [
              ...state.addOPDBill.finalScreen.allHealthCareBillItems.medical[
                lid
              ].schemeMappings,
              schemeMapping,
            ];
          }
        });
        successMessage("Moved item to CGHS Implant in Scheme Bill", 4);
      } else {
        errorMessage("Cannot add CGHS Implant item");
      }
    },
    updateFilterStrings: (
      state,
      action: PayloadAction<{
        searchString: string;
        type:
          | "cghsOPD"
          | "cghsImplant"
          | "cghsMedical"
          | "cghsPackage"
          | "patientPayableOPD"
          | "patientPayableMedical"
          | "healthCareOPD"
          | "healthCareMedical";
      }>
    ) => {
      const { searchString, type } = action.payload;
      switch (type) {
        case "cghsOPD":
          state.addOPDBill.filters.searchFiltersByComponents.scheme.cghs.opd =
            searchString;
          return;
        case "cghsImplant":
          state.addOPDBill.filters.searchFiltersByComponents.scheme.cghs.implant =
            searchString;
          return;
        case "cghsMedical":
          state.addOPDBill.filters.searchFiltersByComponents.scheme.cghs.medical =
            searchString;
          return;
        case "cghsPackage":
          state.addOPDBill.filters.searchFiltersByComponents.scheme.cghs.package =
            searchString;
          return;
        case "healthCareOPD":
          state.addOPDBill.filters.searchFiltersByComponents.healthCare.opd =
            searchString;
          return;
        case "healthCareMedical":
          state.addOPDBill.filters.searchFiltersByComponents.healthCare.medical =
            searchString;
          return;
        case "patientPayableOPD":
          state.addOPDBill.filters.searchFiltersByComponents.scheme.patientPayable.opd =
            searchString;
          return;
        case "patientPayableMedical":
          state.addOPDBill.filters.searchFiltersByComponents.scheme.patientPayable.medical =
            searchString;
          return;
        default:
          console.error("Type not supported");
      }
    },
    updateOPDListFilters: (
      state,
      action: PayloadAction<{ filters: IOPDListFilters }>
    ) => {
      state.opdListFilters = action.payload.filters;
    },
    resetPageToOPDBillListMap: (state) => {
      state.pageToOPDBillList = {};
    },
    updateHealthCareBillHeader: (
      state,
      action: PayloadAction<IOPDHealthCareBillHeader>
    ) => {
      try {
        if (!state.addOPDBill.currState.currHealthCareBillHeaderId) {
          throw new Error("currHealthCareBillHeaderId is invalid");
        }
        state.addOPDBill.finalScreen.healthCareBillIdDataMap[
          state.addOPDBill.currState.currHealthCareBillHeaderId
        ].billHeader = action.payload;
        successMessage("Details updated", 4);
      } catch (error) {
        console.error(error);
        // errorMessage("Error in updating details");
      }
    },
    //Patient details need to be shown from right side i.e, schemeBillHeaderId
    updateSchemeBillHeader: (state, action: PayloadAction<any>) => {
      try {
        if (!state.addOPDBill.currState.currSchemeBillHeaderId) {
          throw new Error("currSchemeBillHeaderId is invalid");
        }
        state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addOPDBill.currState.currSchemeBillHeaderId
        ].billHeader = action.payload;
        successMessage("Details updated", 4);
      } catch (error) {
        console.error(error);
        // errorMessage("Error in updating details");
      }
    },
    addNecessaryDetailsForOPD: (
      state,
      action: PayloadAction<IOPDSchemeBillHeader>
    ) => {
      try {
        state.necessaryDetailsForOPD = action.payload;
      } catch (error) {}
    },
    resetCurrBillState: (state) => {
      state.addOPDBill.currState = {
        currBillMappingId: null,
        currSchemeBillHeaderId: null,
        currHealthCareBillHeaderId: null,
        wizardCurrScreen: 0,
        claimId: null,
        arePackagedItemsHighlighted: false,
        arePatientPayableItemsHighlighted: false,
      };
    },
    resetSchemeBills: (state) => {
      state.addOPDBill.finalScreen.allSchemeBillItems = {
        cghs: {
          implant: {},
          opd: {},
          package: {},
          medical: {},
        },
        patientPayable: {
          opd: {},
          medical: {},
        },
      };
    },
    resetSchemeBillHeaderId: (state, action: PayloadAction<number>) => {
      const key = action.payload;
      if (key) {
        state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[key].billHeader =
          null;
      }
    },
    addExistingBillsDocListToLocalFilesOPD: (
      state,
      action: PayloadAction<Array<IOPDDoc>>
    ) => {
      const billsDocList = action.payload;
      state.addOPDBill.localFiles2 = [...billsDocList];
    },
    updateSchemeBillStatus: (
      state,
      action: PayloadAction<{
        currSchemeBillHeaderId: number | undefined;
        status: string;
      }>
    ) => {
      if (action.payload.currSchemeBillHeaderId) {
        state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
          action.payload.currSchemeBillHeaderId
        ].billHeader = {
          ...state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
            action.payload.currSchemeBillHeaderId
          ].billHeader,
          status: action.payload.status,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getOPDBillList cases
      .addCase(getOPDBillList.pending, (state) => {
        state.isFetchingBillList = true;
        state.pageToOPDBillList = {};
        
      })
      .addCase(
        getOPDBillList.fulfilled,
        (state, action: PayloadAction<IOPDBillListFetchResp>) => {
          state.isFetchingBillList = false;
          state.pageToOPDBillList={};
          state.opdBillListCurrState.totalElements =
            action.payload.totalElements || 1;
          action.payload.billMappingOpdResponseList?.forEach(
            (bill: IOPDBill) => {
              if (
                state.pageToOPDBillList.hasOwnProperty(
                  state.opdListFilters.pageNumber
                )
              ) {
                state.pageToOPDBillList[state.opdListFilters.pageNumber].push(
                  bill
                );
              } else {
                state.pageToOPDBillList[state.opdListFilters.pageNumber] = [
                  bill,
                ];
              }
            }
          );
        }
      )
      .addCase(getOPDBillList.rejected, (state) => {
        state.isFetchingBillList = false;
        state.pageToOPDBillList = {};
      })
      // getOPDQuestionnaire cases
      .addCase(getOPDQuestionnaire.pending, (state) => {
        state.addOPDBill.opdManualQuestions.isFetchingQuestionnaires = true;
      })
      .addCase(
        getOPDQuestionnaire.fulfilled,
        (
          state,
          action: PayloadAction<{
            opdQuestionnaires?: Array<IOPDManualQuestion>;
            errorMessage?: string;
            status: number;
          }>
        ) => {
          // TODO: For every fulfilled action, add error scenario for invalid status

          const { opdQuestionnaires } = action.payload;
          state.addOPDBill.opdManualQuestions.isFetchingQuestionnaires = false;
          opdQuestionnaires &&
            opdQuestionnaires?.forEach(
              (ipQuestionnaire: IOPDManualQuestion) => {
                state.addOPDBill.opdManualQuestions.opdQuestionnaires[
                  ipQuestionnaire.id
                ] = ipQuestionnaire;
              }
            );

          const alreadyAnsweredQuestionsLength =
            state.addOPDBill.opdManualQuestions.answeredQuestionsResponse
              .questionAnswersCollectedResponses.length;
          if (alreadyAnsweredQuestionsLength > 0) {
            state.addOPDBill.opdManualQuestions.currentQuestionId =
              state.addOPDBill.opdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses[
                alreadyAnsweredQuestionsLength - 1
              ].questionId;
          }
        }
      )
      .addCase(getOPDQuestionnaire.rejected, (state) => {
        state.addOPDBill.opdManualQuestions.isFetchingQuestionnaires = false;
      })
      // uploadOPDDoc cases, no need for pending case
      .addCase(
        uploadOPDDoc.fulfilled,
        (
          state,
          action: PayloadAction<{
            docUploadResp?: IOPDDoc;
            status: number;
            errorMessage?: string;
            docType: string;
          }>
        ) => {
          const { docUploadResp, docType } = action.payload;
          state.addOPDBill.localFiles[`${docType}`] = {
            ...state.addOPDBill.localFiles[`${docType}`],
            ...docUploadResp,
            url: docUploadResp?.documentUrl,
          };
          let newData = {
            type: docType,
            originFileObj: docUploadResp?.originFileObj ?? {},
            caseId: docUploadResp?.caseId,
            createdAt: docUploadResp?.createdAt,
            createdBy: docUploadResp?.createdBy,
            documentName: docUploadResp?.documentName,
            documentType: docUploadResp?.documentType,
            documentUrl: docUploadResp?.documentUrl,
            hospitalId: docUploadResp?.hospitalId,
            id: docUploadResp?.id,
            lastModifiedBy: docUploadResp?.lastModifiedBy,
            modifiedAt: docUploadResp?.modifiedAt,
            patientId: docUploadResp?.patientId,
            status: "done",
            uid: docUploadResp?.uid,
            name: docUploadResp?.name,
            url: docUploadResp?.url,
          };

          const mergedObj = {
            ...docUploadResp,
            type: docType,
          };

          if (mergedObj) {
            state.addOPDBill.localFiles2.push(mergedObj);
          }
          state.addOPDBill.localFiles[`${docType}`].status = "done";
          successMessage(`Bill uploaded successfully`);
        }
      )
      .addCase(
        uploadOPDDoc.rejected,
        (
          state,
          action: {
            error: any;
            meta: { arg: { docType: string } };
          }
        ) => {
          const { docType } = action.meta.arg;
          state.addOPDBill.localFiles[`${docType}`].status = "error";
          errorMessage(action.error.message);
        }
      )
      // generateNewOPDBillMapping cases
      .addCase(generateNewOPDBillMapping.pending, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = true;
        state.addOPDBill.errors.errorBillMapping = null;
      })
      .addCase(
        generateNewOPDBillMapping.fulfilled,
        (
          state,
          action: PayloadAction<IOPDBillMappingFetchResp | IOPDErrorResp>
        ) => {
          updateOPDBillMappingsUtil(state, action, false);
          state.addOPDBill.loaders.isFetchOPDBillData = false;

          state.userDetailfilledManually =
            action.payload?.response?.billMappingOpd;
        }
      )
      .addCase(generateNewOPDBillMapping.rejected, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = false;
        state.addOPDBill.errors.errorBillMapping =
          "Cannot fetch opd bill mappings";
      })
      // fetchNewOPDHealthCareBillHeader cases
      .addCase(fetchNewOPDHealthCareBillHeader.pending, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = true;
        state.addOPDBill.errors.errorHealthCareBillHeader = null;
      })
      .addCase(
        fetchNewOPDHealthCareBillHeader.fulfilled,
        (
          state,
          action: PayloadAction<
            IOPDHealthCareBillHeaderFetchResp | IOPDErrorResp
          >
        ) => {
          fetchNewOPDHealthCareBillHeaderUtil(state, action);
        }
      )
      .addCase(fetchNewOPDHealthCareBillHeader.rejected, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = false;
        state.addOPDBill.errors.errorHealthCareBillHeader =
          "Cannot fetch health care bill headers";
      })
      // fetchHealthCarePatientPayable cases
      .addCase(fetchHealthCarePatientPayable.pending, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = true;
        state.addOPDBill.errors.errorHealthCarePatientPayable = null;
      })
      .addCase(
        fetchHealthCarePatientPayable.fulfilled,
        (
          state,
          action: PayloadAction<IOPDPatientPayableFetchResp | IOPDErrorResp>
        ) => {
          state.addOPDBill.loaders.isFetchOPDBillData = false;
          // TODO: Check if this can be avoided by passing a isPatientPayable field?
          const healthCareBillHeaderId =
            action.payload.response?.healthCareBillHeaderId;
          if (
            healthCareBillHeaderId &&
            action.payload.response &&
            action.payload.response.data
          ) {
            state.addOPDBill.finalScreen.healthCareBillIdDataMap[
              healthCareBillHeaderId
            ].patientPayableData = action.payload.response?.data;
          }
        }
      )
      .addCase(fetchHealthCarePatientPayable.rejected, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = false;
        state.addOPDBill.errors.errorHealthCarePatientPayable =
          "Cannot fetch health care patient payable";
      })
      // fetchOPDQuestionAnswerCollection cases
      .addCase(fetchOPDQuestionAnswerCollection.pending, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = true;
        state.addOPDBill.errors.errorQuestionAnswerCollection = null;
      })
      .addCase(
        fetchOPDQuestionAnswerCollection.fulfilled,
        (
          state,
          action: PayloadAction<IOPDFetchQuestionAnswersResp | IOPDErrorResp>
        ) => {
          state.addOPDBill.loaders.isFetchOPDBillData = false;
          if (action.payload.response) {
            state.addOPDBill.opdManualQuestions.answeredQuestionsResponse =
              action.payload.response;
            if (action.payload.response.conclusion !== "") {
              state.addOPDBill.opdManualQuestions.isConclusionGenerated = true;
            }
          }
        }
      )
      .addCase(fetchOPDQuestionAnswerCollection.rejected, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = false;
        state.addOPDBill.errors.errorQuestionAnswerCollection =
          "Cannot fetch opd question answer collection";
      })
      // fetchOPDBillSchemeHeader cases
      .addCase(fetchOPDBillSchemeHeader.pending, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = true;
        state.addOPDBill.errors.errorBillSchemeHeader = null;
      })
      .addCase(
        fetchOPDBillSchemeHeader.fulfilled,
        (
          state,
          action: PayloadAction<IOPDSchemeHeaderFetchResp | IOPDErrorResp>
        ) => {
          try {
            updateSchemeDataUtil(state, action);
          } catch (error) {
            state.addOPDBill.errors.errorBillSchemeHeader =
              "Cannot fetch opd bill scheme headers";
          } finally {
            state.addOPDBill.loaders.isFetchOPDBillData = false;
          }
        }
      )
      .addCase(fetchOPDBillSchemeHeader.rejected, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = false;
        state.addOPDBill.errors.errorBillSchemeHeader =
          "Cannot fetch opd bill scheme headers";
      })

      .addCase(resetSchemeBillItem.pending, (state) => {
        state.addOPDBill.loaders.isResetData = true;
        state.addOPDBill.errors.errorBillSchemeHeader = null;
      })
      .addCase(
        resetSchemeBillItem.fulfilled,
        (
          state,
          action: PayloadAction<IOPDSchemeHeaderFetchResp | IOPDErrorResp>
        ) => {
          try {
            updateSchemeDataUtil(state, action);
          } catch (error) {
            state.addOPDBill.errors.errorBillSchemeHeader =
              "Cannot fetch opd bill scheme headers";
          } finally {
            state.addOPDBill.loaders.isResetData = false;
          }
        }
      )
      .addCase(resetSchemeBillItem.rejected, (state) => {
        state.addOPDBill.loaders.isResetData = false;
        state.addOPDBill.errors.errorBillSchemeHeader =
          "Cannot fetch opd bill scheme headers";
      })

      //post answered questions manually response
      .addCase(submitQuestionAnswersCollection.pending, (state) => {
        state.addOPDBill.errors.errorQuestionAnswerCollection = null;
      })
      .addCase(
        submitQuestionAnswersCollection.fulfilled,
        (
          state,
          action: PayloadAction<IOPDFetchQuestionAnswersResp | IOPDErrorResp>
        ) => {
          if (action.payload.response) {
            state.addOPDBill.opdManualQuestions.answeredQuestionsResponse =
              action.payload.response;
            if (action.payload.response.conclusion !== "") {
              state.addOPDBill.opdManualQuestions.isConclusionGenerated = true;
            }
          }
        }
      )
      .addCase(submitQuestionAnswersCollection.rejected, (state) => {
        state.addOPDBill.errors.errorQuestionAnswerCollection =
          "Cannot post opd question answer collection";
      }) // fetchEditOPDBillMapping cases
      .addCase(fetchEditOPDBillMapping.pending, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = true;
        state.addOPDBill.errors.errorBillMapping = null;
      })
      .addCase(
        fetchEditOPDBillMapping.fulfilled,
        (
          state,
          action: PayloadAction<IOPDBillMappingFetchResp | IOPDErrorResp>
        ) => {
          updateOPDBillMappingsUtil(state, action, true);
          state.addOPDBill.loaders.isFetchOPDBillData = false;
        }
      )
      .addCase(fetchEditOPDBillMapping.rejected, (state) => {
        state.addOPDBill.loaders.isFetchOPDBillData = false;
        state.addOPDBill.errors.errorBillMapping =
          "Cannot fetch opd bill mappings";
      })

      // updateSchemeBills cases
      .addCase(updateSchemeBills.pending, (state) => {
        state.addOPDBill.errors.errorInUpdatingSchemeBillItems = null;
        state.addOPDBill.loaders.isSchemeBillItemsUpdating = true;
      })
      .addCase(
        updateSchemeBills.fulfilled,
        (
          state,
          action: PayloadAction<IOPDSchemeHeaderFetchResp | IOPDErrorResp>
        ) => {
          state.addOPDBill.loaders.isSchemeBillItemsUpdating = false;
          if (action.payload.response) {
            updateSchemeDataUtil(state, action);
            successMessage("Scheme bill items updated successfully", 4);
          } else {
            errorMessage("Cannot update Scheme bill items");
          }
        }
      )
      .addCase(updateSchemeBills.rejected, (state) => {
        state.addOPDBill.errors.errorInUpdatingSchemeBillItems =
          "Cannot update scheme bill items";
        state.addOPDBill.loaders.isSchemeBillItemsUpdating = false;
        successMessage("Error in updating scheme bill items", 4);
      }) // approveOPDBill cases
      .addCase(approveOPDBill.pending, (state) => {
        state.addOPDBill.errors.errorInApprovingOPDBill = null;
        state.addOPDBill.loaders.isApprovingOPDBill = true;
      })
      .addCase(
        approveOPDBill.fulfilled,
        (
          state,
          action: PayloadAction<
            { response: string; status: number } | IOPDErrorResp
          >
        ) => {
          state.addOPDBill.loaders.isApprovingOPDBill = false;
          successMessage("BILL STATUS UPDATED SUCCESSFULLY");
        }
      )
      .addCase(approveOPDBill.rejected, (state) => {
        // state.addOPDBill.errors.errorInApprovingOPDBill =
        //   "Cannot update scheme bill items";
        state.addOPDBill.loaders.isApprovingOPDBill = false;
        errorMessage("ERROR IN UPDATING BILL STATUS");
      })
      .addCase(uploadRelatedDoc.pending, (state) => {
        state.addOPDBill.loaders.isSchemeBillItemsUpdating = true;
      })
      .addCase(
        uploadRelatedDoc.fulfilled,
        (
          state,
          action: PayloadAction<{
            docUploadResp?: IOPDDoc;
            status: number;
            errorMessage?: string;
            docType: string;
          }>
        ) => {
          state.addOPDBill.loaders.isSchemeBillItemsUpdating = true;

          const currentHeaderId =
            state.addOPDBill.currState.currSchemeBillHeaderId;
          if (currentHeaderId) {
            const { docType, docUploadResp } = action.payload;
            const currentBillHeader = {
              ...state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
                currentHeaderId
              ].billHeader,
            };

            if (currentBillHeader) {
              switch (docType) {
                case "DISCHARGE SUMMARY":
                  currentBillHeader.s3DocDischargeSummary =
                    docUploadResp?.documentUrl;
                  break;
                case "REFERRAL LETTER":
                  currentBillHeader.s3DocReferralLetter =
                    docUploadResp?.documentUrl;
                  break;
                case "OTHERS":
                  currentBillHeader.s3DocOthers = docUploadResp?.documentUrl;
                  break;
                default:
                  break;
              }
            }

            state.addOPDBill.finalScreen.schemeBillHeaderIdDataMap[
              currentHeaderId
            ].billHeader = currentBillHeader;
          }

          state.addOPDBill.loaders.isSchemeBillItemsUpdating = false;
        }
      )
      .addCase(uploadRelatedDoc.rejected, (state) => {
        state.addOPDBill.loaders.isSchemeBillItemsUpdating = true;
      });
  },
});

export const {
  clearNecessaryDetailsForOPD,
  updateClaimId,
  updateSchemePackage,
  updateSchemeBillItem,
  updateAddOPDBillWizardCurrScreenNumber,
  updateAddOPDBillWizardCurrScreenValues,
  resetAddOPDBillState,
  addOPDBillUploadedFile,
  removeOPDBillUploadedFile,
  addAnsweredQuestion,
  editAnsweredQuestion,
  removeSchemeBillItem,
  resetAnsweredQuestions,
  toggleHighlightPackagedItems,
  toggleHighlightPatientPayableItems,
  saveRelatedDocLocally,
  updateCardCategory,
  updateObservations,
  updateLidsInPackage,
  addCGHSOPDItemInScheme,
  addCGHSOpdToMedicalItemInScheme,
  addPatientPayableOPDItemInScheme,
  addCGHSImplantItemInScheme,
  addCGHSMedicalItemInScheme,
  addPatientPayableMedicalItemInScheme,
  updateFilterStrings,
  updateOPDListFilters,
  resetPageToOPDBillListMap,
  updateHealthCareBillHeader,
  updateSchemeBillHeader,
  addNecessaryDetailsForOPD,
  resetSchemeBills,
  resetLocalFilesState,
  removeOPDBillUploadedFile2,
  resetSchemeBillHeaderId,
  resetCurrHealthCareBillHeaderId,
  resetCurrSchemeBillHeaderId,
  addExistingBillsDocListToLocalFilesOPD,
  updateSchemeBillStatus,
  resetCurrBillState,
} = opdBillsSlice.actions;
export default opdBillsSlice.reducer;
